/**
 * Profile Completion Utility Functions
 *
 * These functions help determine if a user's profile is complete
 * and provide information about what's missing.
 */

/**
 * Check if user profile is complete
 * A profile is considered complete when all required fields are filled:
 * - Yacht Name
 * - Registration Number -> (Referred in backend as IMO/HULL Number)
 * - Yacht Type (Motor/Sailing)
 * - Hull Type
 * - Length, Beam, Draft
 * - Captain Name, Cell Number, Email
 *
 * @param {Object} userData - User data object from stored session
 * @returns {Boolean} - true if profile is complete, false if incomplete
 */
export const isProfileComplete = (userData) => {
  if (!userData) return false;

  // Check basic yacht information
  const hasYachtName = userData?.yachtName && userData.yachtName !== "";
  const hasImoHullNumber = userData?.imoHullNumber && userData.imoHullNumber !== "";

  // Check yacht specifications (nested object)
  const specs = userData?.yachtSpecifications;
  const hasYachtType = specs?.yachtType && specs.yachtType !== "";
  const hasHullType = specs?.hullType && specs.hullType !== "";
  const hasLength = specs?.length && specs.length !== "";
  const hasBeam = specs?.beam && specs.beam !== "";
  const hasDraft = specs?.draft && specs.draft !== "";

  // Check captain info (nested object)
  const captain = userData?.captainInfo;
  const hasCaptainName = captain?.name && captain.name !== "";
  const hasCellNumber = captain?.cellNumber && captain.cellNumber !== "";
  const hasCaptainEmail = captain?.email && captain.email !== "";

  // All required fields must be present
  return (
    hasYachtName &&
    hasImoHullNumber &&
    hasYachtType &&
    hasHullType &&
    hasLength &&
    hasBeam &&
    hasDraft &&
    hasCaptainName &&
    hasCellNumber &&
    hasCaptainEmail
  );
};

/**
 * Calculate profile completion percentage
 * Includes both required and optional fields to give users
 * a sense of how much information they've provided.
 *
 * @param {Object} userData - User data object from stored session
 * @returns {Number} - Percentage from 0 to 100
 */
export const calculateProfileCompletion = (userData) => {
  if (!userData) return 0;

  const fields = [
    // Basic yacht information (2 fields)
    userData?.yachtName,
    userData?.imoHullNumber,

    // Yacht specifications (5 fields - required)
    userData?.yachtSpecifications?.yachtType,
    userData?.yachtSpecifications?.hullType,
    userData?.yachtSpecifications?.length,
    userData?.yachtSpecifications?.beam,
    userData?.yachtSpecifications?.draft,

    // Captain information (3 fields - required)
    userData?.captainInfo?.name,
    userData?.captainInfo?.cellNumber,
    userData?.captainInfo?.email,

    // Optional but nice to have (3 fields)
    userData?.powerRequired,
    userData?.vesselInsurancePath,
    userData?.vesselRegistrationPath,
  ];

  const completedFields = fields.filter(
    (field) => field !== null && field !== undefined && field !== ""
  ).length;

  const totalFields = fields.length;
  const percentage = (completedFields / totalFields) * 100;

  return Math.round(percentage);
};

/**
 * Get list of missing required fields
 * Useful for displaying specific prompts to users about what they need to complete.
 *
 * @param {Object} userData - User data object from stored session
 * @returns {Array<String>} - Array of human-readable field names that are missing
 */
export const getMissingFields = (userData) => {
  if (!userData) {
    return [
      "Yacht Name",
      "Registration Number",
      "Yacht Type",
      "Hull Type",
      "Length",
      "Beam",
      "Draft",
      "Captain Name",
      "Cell Number",
      "Captain Email",
    ];
  }

  const missingFields = [];

  // Check basic yacht information
  if (!userData?.yachtName || userData.yachtName === "") {
    missingFields.push("Yacht Name");
  }
  if (!userData?.imoHullNumber || userData.imoHullNumber === "") {
    missingFields.push("Registration Number");
  }

  // Check yacht specifications
  const specs = userData?.yachtSpecifications;
  if (!specs?.yachtType || specs.yachtType === "") {
    missingFields.push("Yacht Type");
  }
  if (!specs?.hullType || specs.hullType === "") {
    missingFields.push("Hull Type");
  }
  if (!specs?.length || specs.length === "") {
    missingFields.push("Length");
  }
  if (!specs?.beam || specs.beam === "") {
    missingFields.push("Beam");
  }
  if (!specs?.draft || specs.draft === "") {
    missingFields.push("Draft");
  }

  // Check captain info
  const captain = userData?.captainInfo;
  if (!captain?.name || captain.name === "") {
    missingFields.push("Captain Name");
  }
  if (!captain?.cellNumber || captain.cellNumber === "") {
    missingFields.push("Cell Number");
  }
  if (!captain?.email || captain.email === "") {
    missingFields.push("Captain Email");
  }

  return missingFields;
};

/**
 * Get profile completion status with details
 * Convenience function that returns all profile completion information.
 *
 * @param {Object} userData - User data object from stored session
 * @returns {Object} - Object containing isComplete, percentage, and missingFields
 */
export const getProfileStatus = (userData) => {
  return {
    isComplete: isProfileComplete(userData),
    percentage: calculateProfileCompletion(userData),
    missingFields: getMissingFields(userData),
    missingCount: getMissingFields(userData).length,
  };
};
